import md5 from 'md5';
import { getCookie, setCookie } from '@lucid/utils';

import type { ABTesting, FeatureFlag } from '../../types/types';

interface ABTestHelperProps {
  /** The A/B specific test name. */
  test: string;
  /** A the specific test variations. */
  testBranches?: FeatureFlag.Variations | null;
  /** The name of the key to store the A/B test branch. */
  storageKey: string;
  /** Checks if the A/B test is in development mode. */
  isDevMode: boolean;
  /**  Explicitly define the branches you'll be using to avoid discrepancies with feature flag variation allocations */
  branchesToRender: ABTesting.Variation[];
}

const createVisitorId = () => Math.floor(Math.random() * 2147483647).toString(36);

const getVariationAssignment = ({
  branchesToRender,
  test,
  testBranches,
  storageKey,
  isDevMode,
}: ABTestHelperProps): {
  branch: ABTesting.Variation;
  isTestActive: boolean;
} => {
  if (typeof localStorage === 'undefined') {
    throw new Error('Local Storage not avaialble');
  }

  let branch = 'T-A';
  let isTestActive = false;

  if (testBranches) {
    // Due to more recent changes in the way we query variations of tests, we first need to filter
    // these variations and only work with branches that have a value.
    const filteredBranches = Object.entries(testBranches!).reduce((acc, [key, val]) => {
      if (val !== null && branchesToRender.includes(key.replace(/-/g, '_') as ABTesting.Variation)) {
        acc[key as keyof FeatureFlag.Variations] = val;
      }
      return acc;
    }, {} as FeatureFlag.Variations);
    const activeBranchCount = Object.values(filteredBranches).filter(value => value! > 0).length;

    if (activeBranchCount > 1) {
      isTestActive = true;
    }

    if (isTestActive || isDevMode) {
      const localStorageBranch = localStorage.getItem(storageKey);
      if (localStorageBranch && Object.keys(filteredBranches).includes(localStorageBranch)) {
        return { branch: localStorageBranch as ABTesting.Variation, isTestActive };
      }
    }

    const totalWeight = Object.values(filteredBranches).reduce<number>((thisSum, a = 0) => thisSum + a!, 0);
    let abVisitorId = getCookie('ab_id') || localStorage.getItem('abVisitorId');
    if (!abVisitorId) {
      abVisitorId = createVisitorId();
      localStorage.setItem('abVisitorId', abVisitorId);
      setCookie('ab_id', abVisitorId, {
        expires: 60 * 60 * 24 * 365.25 * 5,
        path: '/',
      });
    }

    const hash = md5(`${abVisitorId}|${test}`, { asBytes: true });
    let sum = 0;
    for (let i = 0; i < 8; i += 1) {
      sum <<= 8;
      sum += hash[i];
    }
    sum = Math.abs(sum);
    let weightLeft = sum % totalWeight;
    let last = '';
    const keys = Object.keys(filteredBranches);
    for (const option of keys) {
      const weight = filteredBranches[option as ABTesting.Variation];
      weightLeft -= weight!;
      last = option;
      if (weightLeft < 0) {
        break;
      }
    }

    branch = last;
    localStorage.setItem(storageKey, branch);
  } else {
    throw new Error(`Test named: ${test} is invalid or not present in the test config object`);
  }

  return { branch: branch as ABTesting.Variation, isTestActive };
};

export { createVisitorId, getVariationAssignment };
